import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Prefs {
  email_goal_added: boolean;
  email_board_meeting: boolean;
  email_vision_update: boolean;
  email_memory_update: boolean;
  push_enabled: boolean;
}

// VAPID public key fetched from backend
let cachedVapidKey: string | null = null;
async function getVapidPublicKey(): Promise<string> {
  if (cachedVapidKey) return cachedVapidKey;
  const { data, error } = await supabase.functions.invoke("get-vapid-key");
  if (error || !data?.vapidPublicKey) return "";
  cachedVapidKey = data.vapidPublicKey;
  return cachedVapidKey!;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function NotificationSettings() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>({
    email_goal_added: true,
    email_board_meeting: true,
    email_vision_update: false,
    email_memory_update: false,
    push_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [testingSending, setTestingSending] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("notification_preferences")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setPrefs({
            email_goal_added: data.email_goal_added,
            email_board_meeting: data.email_board_meeting,
            email_vision_update: data.email_vision_update,
            email_memory_update: data.email_memory_update,
            push_enabled: data.push_enabled,
          });
        }
        setLoading(false);
      });
  }, [user]);

  const savePrefs = async (updated: Partial<Prefs>, extraFields?: Record<string, any>) => {
    const newPrefs = { ...prefs, ...updated };
    setPrefs(newPrefs);
    if (!user) return;

    const { error } = await supabase
      .from("notification_preferences")
      .upsert(
        { user_id: user.id, ...newPrefs, ...extraFields },
        { onConflict: "user_id" }
      );
    if (error) {
      toast.error("Couldn't save", { description: error.message });
    }
  };

  const subscribeToPush = async () => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      toast.error("Push notifications not supported on this device");
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      toast.error("Permission denied", { description: "You can enable it in your browser settings." });
      return;
    }

    try {
      // Register the push service worker
      const registration = await navigator.serviceWorker.register("/sw-push.js");
      await navigator.serviceWorker.ready;

      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) {
        toast.error("Push not configured", { description: "VAPID key is missing." });
        return;
      }

      const subscription = await (registration as any).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const subJson = subscription.toJSON();
      await savePrefs({ push_enabled: true }, {
        push_subscription: {
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        },
      });
      toast.success("Push notifications enabled 🔔");
    } catch (err: any) {
      console.error("Push subscription failed:", err);
      toast.error("Push setup failed", { description: err.message });
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration("/sw-push.js");
      if (registration) {
        const subscription = await (registration as any).pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      }
    } catch (e) {
      console.error("Unsubscribe error:", e);
    }
    await savePrefs({ push_enabled: false }, { push_subscription: null });
  };

  if (loading) return null;

  const toggles: { key: keyof Prefs; label: string; icon: React.ElementType }[] = [
    { key: "email_goal_added", label: "Email when a goal is added", icon: Mail },
    { key: "email_board_meeting", label: "Email when a reflection is submitted", icon: Mail },
    { key: "email_vision_update", label: "Email on vision board updates", icon: Mail },
    { key: "email_memory_update", label: "Email on new memories", icon: Mail },
  ];

  const sendTestNotification = async (notifType: string) => {
    if (!user) return;
    setTestingSending(notifType);
    try {
      const { data: prof } = await supabase.from("profiles").select("workspace_id, preferred_name, full_name").eq("user_id", user.id).single();
      if (!prof?.workspace_id) { toast.error("No workspace found"); setTestingSending(null); return; }
      const displayName = prof.preferred_name || prof.full_name || "You";
      const { error } = await supabase.functions.invoke("send-notification-email", {
        body: { type: "test", workspaceId: prof.workspace_id, actorName: displayName, details: { testType: notifType } },
      });
      if (error) toast.error("Test failed", { description: error.message });
      else toast.success("Test notification sent! Check the bell icon 🔔");
    } catch (e: any) {
      toast.error("Test failed", { description: e.message });
    }
    setTestingSending(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="bg-card border border-border rounded-xl p-6 space-y-4"
    >
      <div className="flex items-center gap-2 text-primary">
        <Bell className="w-5 h-5" />
        <h2 className="font-display text-xl font-semibold">Notifications</h2>
      </div>

      <div className="space-y-3">
        {toggles.map((t) => (
          <div key={t.key} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <t.icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-foreground truncate">{t.label}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => sendTestNotification(t.key)}
                disabled={testingSending === t.key}
                className="text-xs text-primary hover:underline disabled:opacity-50"
              >
                {testingSending === t.key ? "Sending..." : "Test"}
              </button>
              <button
                onClick={() => savePrefs({ [t.key]: !prefs[t.key] })}
                className={`w-10 h-6 rounded-full transition-colors relative ${
                  prefs[t.key] ? "bg-primary" : "bg-secondary"
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-primary-foreground transition-transform ${
                    prefs[t.key] ? "left-5" : "left-1"
                  }`}
                />
              </button>
            </div>
          </div>
        ))}

        {/* Push toggle */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Bell className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm text-foreground truncate">Push notifications</span>
          </div>
          <button
            onClick={() => {
              if (!prefs.push_enabled) {
                subscribeToPush();
              } else {
                unsubscribeFromPush();
              }
            }}
            className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${
              prefs.push_enabled ? "bg-primary" : "bg-secondary"
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-primary-foreground transition-transform ${
                prefs.push_enabled ? "left-5" : "left-1"
              }`}
            />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
